import { humanPath } from "./diagnostics";

export type ChangeKind = "added" | "changed" | "removed";
export interface ValueChange {
  kind: ChangeKind;
  path: (string | number)[];
  /** Readable location, e.g. "Tabs › 2 › Name". */
  where: string;
  before?: string;
  after?: string;
}
export interface ChangeSummary {
  added: number;
  changed: number;
  removed: number;
  total: number;
  /** The first changes in document order (capped for display). */
  items: ValueChange[];
  /** True when more changes exist than `items` lists. */
  truncated: boolean;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

/** Short display form of a value for change lists. */
export function preview(value: unknown, max = 60): string {
  if (value === undefined) return "";
  if (typeof value === "string")
    return JSON.stringify(
      value.length > max ? value.slice(0, max) + "…" : value,
    );
  if (Array.isArray(value))
    return `${value.length} item${value.length === 1 ? "" : "s"}`;
  if (isObject(value)) {
    const keys = Object.keys(value).length;
    return `${keys} propert${keys === 1 ? "y" : "ies"}`;
  }
  return String(value);
}

/**
 * Structural difference between two JSON values. Objects compare by key and
 * lists by position; a changed scalar or a type change counts as one change.
 */
export function summarizeChanges(
  before: unknown,
  after: unknown,
  limit = 40,
): ChangeSummary {
  const summary: ChangeSummary = {
    added: 0,
    changed: 0,
    removed: 0,
    total: 0,
    items: [],
    truncated: false,
  };
  const record = (
    kind: ChangeKind,
    path: (string | number)[],
    a?: unknown,
    b?: unknown,
  ) => {
    summary[kind]++;
    summary.total++;
    if (summary.items.length < limit)
      summary.items.push({
        kind,
        path,
        where: humanPath(path) || "Whole value",
        before: kind === "added" ? undefined : preview(a),
        after: kind === "removed" ? undefined : preview(b),
      });
    else summary.truncated = true;
  };
  const walk = (a: unknown, b: unknown, path: (string | number)[]) => {
    if (Object.is(a, b)) return;
    if (Array.isArray(a) && Array.isArray(b)) {
      const n = Math.max(a.length, b.length);
      for (let i = 0; i < n; i++) {
        if (i >= a.length) record("added", [...path, i], undefined, b[i]);
        else if (i >= b.length) record("removed", [...path, i], a[i]);
        else walk(a[i], b[i], [...path, i]);
      }
      return;
    }
    if (isObject(a) && isObject(b)) {
      for (const key of Object.keys(a))
        if (!Object.hasOwn(b, key)) record("removed", [...path, key], a[key]);
        else walk(a[key], b[key], [...path, key]);
      for (const key of Object.keys(b))
        if (!Object.hasOwn(a, key))
          record("added", [...path, key], undefined, b[key]);
      return;
    }
    record("changed", path, a, b);
  };
  walk(before, after, []);
  return summary;
}

/** "3 changes: 1 added, 2 changed" */
export function describeSummary(summary: ChangeSummary) {
  if (!summary.total) return "No changes";
  const parts = (
    [
      ["added", summary.added],
      ["changed", summary.changed],
      ["removed", summary.removed],
    ] as const
  )
    .filter(([, n]) => n)
    .map(([label, n]) => `${n} ${label}`);
  return `${summary.total} change${summary.total === 1 ? "" : "s"}: ${parts.join(", ")}`;
}
